/** Shared PDF layout constants for classic and two-column exports */
export const PT_TO_MM = 0.352778

export const PDF_CONFIG = {
  pageWidth: 210,
  pageHeight: 297,
  marginPt: {
    left: 43,
    right: 43,
    top: 36,
    bottom: 36,
  },
  fontSize: {
    name: 22,
    sectionHeader: 10,
    jobTitle: 12,
    /** Teal line 1: company name (slightly larger than location/dates). */
    companyLineCompany: 10.5,
    /** Teal line 1: location, dates, bullets on that line (slightly smaller, still bold). */
    companyLineMeta: 9.5,
    company: 10,
    body: 10,
    contact: 9,
    bullet: 10,
  },
  lineHeight: {
    name: 1.17,
    jobTitle: 1.17,
    body: 1.17,
    bullet: 1.17,
  },
  colors: {
    accent: [245, 118, 1] as [number, number, number],
    black: [0, 0, 0] as [number, number, number],
    gray: [100, 100, 100] as [number, number, number],
    /** Two-column preview/PDF: name — #00786c (same as section headers) */
    twoColumnName: [0, 120, 108] as [number, number, number],
    /** Two-column section headers (Work Experience, Skills, …) — #00786c */
    twoColumnSection: [0, 120, 108] as [number, number, number],
  },
  sectionHeader: {
    marginTop: 24,
    gapToLine: 3,
    lineThickness: 0.75,
    gapToContent: 3,
  },
  spacing: {
    itemGap: 3,
    bulletIndent: 6,
    lineGap: 1,
  },
}

/**
 * Teal two-column proportions (reference canvas 730px):
 * left 420.82, right 150.3, inter-column gap 5% of canvas (36.5px).
 * Column widths are shares of the content band (left + gap + right = 607.62px on ref canvas).
 */
const TEAL_TWO_COLUMN_REF_TOTAL_PX = 730
const TEAL_TWO_COLUMN_REF_LEFT_PX = 420.82
const TEAL_TWO_COLUMN_REF_RIGHT_PX = 150.3
const TEAL_TWO_COLUMN_REF_GAP_PX = TEAL_TWO_COLUMN_REF_TOTAL_PX * 0.05
const TEAL_TWO_COLUMN_REF_CONTENT_PX =
  TEAL_TWO_COLUMN_REF_LEFT_PX + TEAL_TWO_COLUMN_REF_GAP_PX + TEAL_TWO_COLUMN_REF_RIGHT_PX

/** Left column: 420.82 / 607.62 ≈ 69.26% of printable content width. */
export const TWO_COLUMN_LEFT_COL_RATIO =
  TEAL_TWO_COLUMN_REF_LEFT_PX / TEAL_TWO_COLUMN_REF_CONTENT_PX
/** Right column: 150.3 / 607.62 ≈ 24.74% of printable content width. */
export const TWO_COLUMN_RIGHT_COL_RATIO =
  TEAL_TWO_COLUMN_REF_RIGHT_PX / TEAL_TWO_COLUMN_REF_CONTENT_PX
/** Gap: 36.5 / 607.62 ≈ 6.01% of printable content width (5% of Teal canvas). */
export const TWO_COLUMN_COL_GAP_RATIO =
  TEAL_TWO_COLUMN_REF_GAP_PX / TEAL_TWO_COLUMN_REF_CONTENT_PX
/** Documented Teal inter-column gap: 5% of full resume canvas width. */
export const TWO_COLUMN_COL_GAP_OF_CANVAS_RATIO = 0.05

/** @deprecated use TWO_COLUMN_COL_GAP_RATIO × content width; kept for legacy callers */
export const TWO_COLUMN_COL_GAP_MM = (() => {
  const ml = PDF_CONFIG.marginPt.left * PT_TO_MM
  const mr = PDF_CONFIG.marginPt.right * PT_TO_MM
  const contentMm = PDF_CONFIG.pageWidth - ml - mr
  return contentMm * TWO_COLUMN_COL_GAP_RATIO
})()

export function twoColumnColgroupPercents(): { left: string; gap: string; right: string } {
  const pct = (r: number) => `${(r * 100).toFixed(4)}%`
  return {
    left: pct(TWO_COLUMN_LEFT_COL_RATIO),
    gap: pct(TWO_COLUMN_COL_GAP_RATIO),
    right: pct(TWO_COLUMN_RIGHT_COL_RATIO),
  }
}
/** Tailwind `mt-4` on `.paysafe-columns` — gap from divider line to column row (1rem @ 16px). */
export const TWO_COLUMN_DIVIDER_TO_ROW_MM = (16 * 25.4) / 96
/** Extra pad before target/summary body — preview `text-base` line box vs 12pt PDF baseline. */
export const TWO_COLUMN_LEAD_TEXT_TOP_PAD_MM = (8 * 25.4) / 96
/** Tailwind `mb-3` between summary `<p>` blocks in PaysafeTwoColumnPreview. */
export const TWO_COLUMN_SUMMARY_PARAGRAPH_GAP_MM = (12 * 25.4) / 96
/** Tailwind `mb-2` between multiple target title lines. */
export const TWO_COLUMN_TARGET_ITEM_GAP_MM = (8 * 25.4) / 96
/**
 * Tailwind `mb-6` (24px @ 96dpi): gap from end of a plain title block to next body.
 * Left: target title → professional summary (no rule line in between).
 */
export const TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM = (24 * 25.4) / 96
/** Section header: `mb-1` between title text (SKILLS) and rule line — preview + PDF. */
export const TWO_COLUMN_SECTION_TITLE_TO_LINE_GAP_MM = (4 * 25.4) / 96
/** Section header rule thickness (1px). */
export const TWO_COLUMN_SECTION_RULE_THICKNESS_MM = (1 * 25.4) / 96
/**
 * Section header: gap from rule line → first body line (Analytics tools:).
 * Chosen so title→line→body totals `TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM` (same as target→summary).
 */
export const TWO_COLUMN_SECTION_LINE_TO_BODY_GAP_MM =
  TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM -
  TWO_COLUMN_SECTION_TITLE_TO_LINE_GAP_MM -
  TWO_COLUMN_SECTION_RULE_THICKNESS_MM
/** @deprecated alias — use TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM */
export const TWO_COLUMN_TARGET_TO_SUMMARY_GAP_MM = TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM
/** Tailwind `mt-4` on summary block when there is no target title. */
export const TWO_COLUMN_SUMMARY_ONLY_TOP_GAP_MM = TWO_COLUMN_DIVIDER_TO_ROW_MM
/** Tailwind `mb-6` after lead block (summary) before work experience. */
export const TWO_COLUMN_LEAD_BLOCK_BOTTOM_GAP_MM = (24 * 25.4) / 96
/** @deprecated alias — use TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM */
export const TWO_COLUMN_SECTION_HEADER_AFTER_MM = TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM
/** @deprecated use TWO_COLUMN_DIVIDER_TO_ROW_MM */
export const TWO_COLUMN_COLUMNS_ROW_GAP_MM = TWO_COLUMN_DIVIDER_TO_ROW_MM
/** Approximate cap-height ratio for Helvetica alignment between lead title and section header. */
export const PDF_CAP_HEIGHT_RATIO = 0.75

/** Section header color for two-column template (preview, PDF, DOCX). */
export const TWO_COLUMN_SECTION_COLOR_HEX = '#00786c'
